import React, { Fragment, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import QuickDial from "~/components/QuickDial";
import { styled } from "@mui/material/styles";
import { propOr } from "ramda";
import clsx from "clsx";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LinkIcon from "@mui/icons-material/Link";

import EmbeddedViewer from "~/components/EmbeddedViewer";
import IntrinsicAspectRatio from "~/components/IntrinsicAspectRatio";
import { getIconByListIconType } from "~/constants/materials";
import generateTeacherMaterialGetters from "~/libs/dls";
import {
  getQuickDialActions,
  getPresentationMarkerAction,
  getMaterialNoteAction,
  getOpenAction
} from "~/utils/quickdial";
import { isDeepLink, getDeepLinkWindowName, isAppleiPadDevice, getRelativeDeeplink } from "~/utils";
import { hasMissingUserLicense, isUnavailableInDemo } from "~/utils/dlsmaterial";

import IconButton from "@mui/material/IconButton";
import StarIcon from "@mui/icons-material/Star";

import MaterialPreviewHeader from "./MaterialPreviewHeader";
import MaterialPreviewIcon from "./MaterialPreviewIcon";
import { Create } from "@mui/icons-material";

const PresentationMarkerWrapper = styled("div")`
  display: inline-block;
  cursor: pointer;
  pointer-events: auto;
  position: absolute;
  top: 0;
  right: 0;
  left: auto !important;
`;

const PresentationMarkerIcon = styled(StarIcon)`
  font-size: 3.5rem !important;
  color: #1e85a3;
`;

const hasRenderType = material => {
  const renderType = propOr("", "renderType", material);
  return renderType !== "";
};

const TransparentButton = styled(IconButton)({
  "&.MuiIconButton-root": {
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent"
    },
    padding: 8
  }
});

const MaterialPreview = styled("div")(() => {
  return {
    width: "200px",
    position: "relative",
    marginRight: "1em",
    marginBottom: "2em"
    // FIXME:
    // "& + &": {
    //   marginTop: "4em"
    // }
  };
});

const StyledMaterialPreview = styled(MaterialPreview, {
  shouldForwardProp: prop => true
})(props => {
  if (props.istTitel) {
    return {
      gridColumn: "1 / -1",
      width: "100%",
      display: "flex",
      flexDirection: "row-reverse",
      justifyContent: "flex-end",
      alignItems: "flex-end",
      marginBottom: 0,
      flexWrap: "wrap"
    };
  }
  return {};
});

const Column = styled("div")(() => ({
  backgroundColor: "#404040",
  backgroundImage: "url(/static/images/texture.png)",
  overflowY: "auto",
  width: "100%"
}));

const Title = styled("h3")(({ theme }) => ({
  color: "white",
  padding: theme.spacing(1, 0),
  margin: 0,
  height: "auto",
  gridColumn: "1 / -1",
  fontSize: theme.typography.pxToRem(18),
  fontWeight: "normal"
}));

const EmptyPreviews = styled("div")`
  padding: 8px 16px;
`;

const Previews = styled("div")`
  padding: 8px 28px 28px;
  display: grid;
  grid-template-columns: repeat(auto-fill, 200px);
  grid-gap: 1em;
  justify-content: space-evenly;
`;

const MaterialTitelDivider = styled("div")`
  grid-column: 1 / -1;
  width: 100%;
  background-color: white;
  height: 3px;
`;

const MaterialPreviewContent = styled(IntrinsicAspectRatio)`
  cursor: ${props => (props.isClickable ? "pointer" : "auto")};
  width: ${props => (props.istTitel ? "100px" : "auto")};
  flex-shrink: 0;
  margin-right: ${props => (props.istTitel ? "8px" : 0)};
`;

const Image = styled("img")`
  max-width: 100%;
  height: auto;
  vertical-align: baseline;
`;

const FullHeight = styled("div")`
  height: 100%;
`;

const isPresentationActive = (presentationMarker, activePages, category) => {
  if (!presentationMarker || !presentationMarker.pages) {
    return false;
  }

  if (category.type === "page") {
    return presentationMarker.pages.some(page => activePages.includes(page));
  } else if (category.type === "book") {
    return presentationMarker.book;
  }

  return false;
};

function getLockIcon(material) {
  if (isUnavailableInDemo(material)) {
    return <Image src="/static/images/demoartikel.png" />;
  }
  if (hasMissingUserLicense(material)) {
    return <Image src="/static/images/lizenzartikel.png" />;
  }
  return null;
}

function TeacherColumn({
  className,
  showModal,
  materials,
  category,
  classes,
  activePages,
  addPresentationMarker,
  updatePresentationMarker,
  openNewDlsUserMaterialModal,
  openShowDlsUserMaterialModal,
  isPresentationMode,
  openViewerModal
}) {
  let content = null;
  const columnRef = useRef(null);

  const catDep = (category || {}).fullTitle;

  useEffect(() => {
    if (columnRef.current) columnRef.current.scrollTo(0, 0);
  }, [catDep]);

  // const isdemo = useMemo(
  //   () => window.getClientConfiguration(false, ["book", "settings", "licensetype_settings", "features", "isdemo"]),
  //   []
  // );

  // const demofullforgroup = useMemo(
  //   () =>
  //     window.getClientConfiguration(false, [
  //       "book",
  //       "settings",
  //       "licensetype_settings",
  //       "features",
  //       "demofullforgroup"
  //     ]),
  //   []
  // );

  // const isDemoFullForGroup = useMemo(
  //   () => window.getClientConfiguration([], ["profile", "groups"]).includes(demofullforgroup),
  //   [demofullforgroup]
  // );

  // console.log("isDemo", isdemo);
  // console.log("demofullforgroup", demofullforgroup);
  // console.log("isDemoFullForGroup", isDemoFullForGroup);

  function renderViewerPreview(material) {
    const DLS = generateTeacherMaterialGetters(material);

    return (
      <FullHeight>
        <EmbeddedViewer
          key={material._id}
          channel="embedded"
          material={material}
          getThumbnailPath={DLS.getThumbnailPath}
          getImagePath={DLS.getImagePath}
          getQuickDialActions={(zoomController, currentPage) =>
            getQuickDialActions({
              material,
              showModal,
              activePages,
              addPresentationMarker,
              updatePresentationMarker,
              zoomController,
              category,
              printOrigin: "embedded",
              isPresentationMode,
              currentPage
            })
          }
        />
      </FullHeight>
    );
  }

  function renderListIcon(material, DLS) {
    const deepLink = isDeepLink(material.displayAction);
    const showDeepLinkedThumbnail = deepLink && material.listIcon !== "THUMBNAIL";

    if (!deepLink && material.listIcon !== "THUMBNAIL") {
      const ListIcon = getIconByListIconType(material.listIcon);
      return getLockIcon(material) || <MaterialPreviewIcon Icon={ListIcon} />;
    }

    const dataSrc = showDeepLinkedThumbnail ? material.listIcon : DLS.getListThumbnailPath();
    return (
      <>
        <Image className="lazyload" data-sizes="auto" data-src={dataSrc} />
        {getLockIcon(material)}
      </>
    );
  }

  function renderListPreview() {
    return (
      <Previews>
        <Title>{category.title}</Title>
        {materials.map(material => {
          const DLS = generateTeacherMaterialGetters(material);

          // console.log("material", material);

          let primaryHeaderText = material.name;
          let secondaryHeaderText = `${material.title}${
            material.renderType === "docviewer" && material.pagesCount > 1 ? ` (${material.pagesCount} S.)` : ""
          }`;

          if (category.kind === "presentation_materials") {
            const materialTitle = material.name || material.title;
            primaryHeaderText = material.Werk || materialTitle;
            secondaryHeaderText = material.Werk ? materialTitle : null;
          }

          if (material.type === "dlsUserMaterial") {
            primaryHeaderText = material.title;
            secondaryHeaderText = "Eigene Materialien";
          }

          const getOnClickHandler = () => {
            const renderType = propOr("", "renderType", material);
            if (renderType === "" || hasMissingUserLicense(material)) {
              return null;
            }
            return event => {
              event.stopPropagation();

              if (material.type === "dlsUserMaterial") {
                openShowDlsUserMaterialModal(material)(event);
              } else if (renderType === "docviewer") {
                openViewerModal({
                  fullScreen: true,
                  additionalProps: { materialId: material._id }
                });
              } else if (renderType === "link") {
                if (isDeepLink(material.displayAction)) {
                  const windowName = getDeepLinkWindowName(material.displayAction);

                  if (isAppleiPadDevice() && window.openWindows.hasOwnProperty(windowName)) {
                    try {
                      window.openWindows[windowName].close();
                    } catch (e) {}
                  }

                  window.openWindows[windowName] = window.open(getRelativeDeeplink(material.displayAction), windowName);
                } else {
                  showModal("MATERIAL_RENDERER", {
                    material: {
                      renderType: material.renderType,
                      sourcePath: material.displayAction,
                      linkText: material.name,
                      name: material.name,
                      title: material.title
                    }
                  });
                }
              } else {
                showModal("MATERIAL_RENDERER", {
                  material: {
                    renderType: material.renderType,
                    sourcePath: DLS.getStaticFilePath(material.displayAction),
                    linkText: material.name,
                    name: material.name,
                    title: material.title
                  }
                });
              }
            };
          };

          return (
            <Fragment key={`dls_material_${material._id}`}>
              <StyledMaterialPreview isClickable={hasRenderType(material)} istTitel={material.istTitel} name="fuba">
                <MaterialPreviewHeader
                  primaryText={primaryHeaderText}
                  secondaryText={secondaryHeaderText}
                  istTitel={material.istTitel}
                />

                <QuickDial
                  istTitel={material.istTitel}
                  positionSpacing={1}
                  actions={getQuickDialActions({
                    material,
                    showModal,
                    activePages,
                    addPresentationMarker,
                    updatePresentationMarker,
                    category,
                    printOrigin: "teacher",
                    isPresentationMode,
                    hasMissingUserLicense: hasMissingUserLicense(material),
                    openFunc: getOnClickHandler()
                  })}
                />

                <MaterialPreviewContent
                  ratio="DIN-A"
                  orientation="portrait"
                  istTitel={material.istTitel}
                  isClickable={hasRenderType(material) && !hasMissingUserLicense(material)}
                  onClick={getOnClickHandler()}
                >
                  {renderListIcon(material, DLS)}
                  {isPresentationActive(material.presentationMarker, activePages, category) && (
                    <PresentationMarkerWrapper>
                      <TransparentButton size="large">
                        <PresentationMarkerIcon />
                      </TransparentButton>
                    </PresentationMarkerWrapper>
                  )}
                </MaterialPreviewContent>
              </StyledMaterialPreview>
              {material.istTitel && <MaterialTitelDivider />}
            </Fragment>
          );
        })}
      </Previews>
    );
  }

  function renderUserListPreview() {
    return (
      <Previews>
        <Title>{category.title}</Title>
        <MaterialPreview isClickable={true}>
          <MaterialPreviewHeader primaryText="Material hinzufügen" />
          <MaterialPreviewContent
            ratio="DIN-A"
            orientation="portrait"
            isClickable={true}
            onClick={openNewDlsUserMaterialModal}
          >
            <MaterialPreviewIcon Icon={AddCircleOutlineIcon} />
          </MaterialPreviewContent>
        </MaterialPreview>

        {materials.map(material => {
          return (
            <MaterialPreview isClickable={true} key={`dls_user_material_${material._id}`}>
              <MaterialPreviewHeader primaryText={material.name} secondaryText={material.title} />

              <MaterialPreviewContent
                ratio="DIN-A"
                orientation="portrait"
                isClickable={true}
                onClick={openShowDlsUserMaterialModal(material)}
              >
                <MaterialPreviewIcon Icon={LinkIcon} />
                {isPresentationActive(material.presentationMarker, activePages, category) && (
                  <PresentationMarkerWrapper>
                    <TransparentButton size="large">
                      <PresentationMarkerIcon />
                    </TransparentButton>
                  </PresentationMarkerWrapper>
                )}
              </MaterialPreviewContent>
              <QuickDial
                positionSpacing={1}
                actions={[
                  getOpenAction(openShowDlsUserMaterialModal(material)),
                  getMaterialNoteAction(material, showModal),
                  getPresentationMarkerAction(
                    material,
                    activePages,
                    addPresentationMarker,
                    updatePresentationMarker,
                    category,
                    isPresentationMode
                  )
                ]}
              />
            </MaterialPreview>
          );
        })}
      </Previews>
    );
  }

  if (category && category.isStatic) {
    if (category.kind && category.kind === "custom_materials") {
      content = renderUserListPreview();
    } else {
      content = renderListPreview();
    }
  } else if (category && materials.length === 0) {
    content = (
      <EmptyPreviews>
        <Title>{category.title}</Title>
        <Title as={"h4"}>Keine Materialien vorhanden</Title>
      </EmptyPreviews>
    );
  } else if (category && category.view && category.view === "list") {
    content = renderListPreview();
  } else if (category && category.view && category.view === "single") {
    if (materials.length > 1 || hasMissingUserLicense(materials[0])) {
      content = renderListPreview();
    } else {
      let material = materials[0];

      if (material.renderType === "docviewer") {
        content = renderViewerPreview(material);
      }
    }
  } else {
    content = (
      <EmptyPreviews>
        <Title>Keine Kategorie ausgewählt</Title>
      </EmptyPreviews>
    );
  }

  return (
    <Column className={className} ref={columnRef}>
      {content}
    </Column>
  );
}

TeacherColumn.propTypes = {
  className: PropTypes.string,
  showModal: PropTypes.func.isRequired,
  materials: PropTypes.array.isRequired,
  category: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  activePages: PropTypes.array.isRequired,
  addPresentationMarker: PropTypes.func.isRequired,
  updatePresentationMarker: PropTypes.func.isRequired,
  openNewDlsUserMaterialModal: PropTypes.func.isRequired,
  openShowDlsUserMaterialModal: PropTypes.func.isRequired,
  isPresentationMode: PropTypes.bool,
  openViewerModal: PropTypes.func.isRequired
};

export default TeacherColumn;
